import React, { useCallback } from 'react';
import { QueryKey } from '@tanstack/react-query';
import classNames from 'classnames';
import { message, Modal } from 'antd';
import { AWAssetDto, SubmitDecisionDto } from '@api/Api';
import { useDecisionApprovalWorkflow } from '@hooks/queries/approval';
import { CrossIcon, SuccessIcon } from '@icons';
import './ApprovalBulkActions.less';

type ApprovalBulkDecisionProps = {
  assets: AWAssetDto[];
  currentStageId: string;
  approvalWorkflowId: string;
  password: string | null;
  queryKey?: QueryKey;
  onClose: () => void;
};

const decisions = [
  {
    label: 'Approved',
    backgroundColor: '#92D1BA',
    key: 'approved'
  },
  {
    label: 'Approved with feedback',
    backgroundColor: '#BFC5F4',
    key: 'approved_with_feedback'
  },
  {
    label: 'Changes required',
    backgroundColor: '#F7CAB0',
    key: 'changes_required'
  }
];

export default function ApprovalBulkDecision({
  assets,
  approvalWorkflowId,
  currentStageId,
  password,
  queryKey,
  onClose
}: ApprovalBulkDecisionProps) {
  const {
    mutateAsync: decisionApprovalWorkflow,
    isLoading: isLoadingDecisionApprovalWorkflow
  } = useDecisionApprovalWorkflow({ queryKey });

  const selectDecision = useCallback(
    async (decisionKey: SubmitDecisionDto['decision'], confirmed = false) => {
      if (!assets.length) return;
      if (!confirmed) {
        Modal.confirm({
          onOk: () => selectDecision(decisionKey, true),
          width: 500,
          icon: null,
          centered: true,
          okText: 'Yes, submit',
          cancelText: 'No, continue review',
          closable: true,
          maskClosable: true,
          closeIcon: <CrossIcon />,
          content: (
            <div className="modal_container">
              <h2
                className="modal_container__title"
                style={{ fontSize: 22, marginBottom: '0.5em' }}
              >
                Is this your final decision?
              </h2>
              <p className="modal_container__subtitle">
                Once submitted, you will not be able to leave additional
                comments or change your decision.
              </p>
            </div>
          )
        });
        return;
      }
      if (isLoadingDecisionApprovalWorkflow) return;
      try {
        await decisionApprovalWorkflow({
          id: approvalWorkflowId,
          stageId: currentStageId,
          versionIds: assets.map((item) => item.versionId),
          password,
          decision: decisionKey
        });
        const decisionLabel = decisions.find(
          (x) => x.key === decisionKey
        )?.label;
        onClose();
        message.success({
          content: `Selected media has been marked as ${decisionLabel}`,
          className: 'message-dark-modal',
          icon: <SuccessIcon className="anticon" />,
          duration: 2
        });
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Something went wrong');
      }
    },
    [
      assets,
      password,
      approvalWorkflowId,
      currentStageId,
      isLoadingDecisionApprovalWorkflow,
      decisionApprovalWorkflow,
      onClose
    ]
  );

  return (
    <div
      className={classNames('approval-bulk-actions', {
        'approval-bulk-actions--active': !!assets?.length
      })}
    >
      <div className="approval-bulk-actions_top">
        <div className="approval-bulk-actions_info">
          <div className="approval-bulk-actions_info-icon" />
          <div className="approval-bulk-actions_info-text">
            {assets?.length} media selected
          </div>
        </div>
        <div className="approval-bulk-actions_close" onClick={onClose} />
      </div>
      <div className="approval-bulk-actions_actions">
        {decisions.map((item) => (
          <div
            key={item.key}
            className={classNames('approval-bulk-actions_actions-item', {
              'approval-bulk-actions_actions-item--loading':
                isLoadingDecisionApprovalWorkflow
            })}
            onClick={() =>
              selectDecision(item.key as SubmitDecisionDto['decision'])
            }
          >
            <div
              className="approval-bulk-actions_actions-icon"
              style={{ backgroundColor: item.backgroundColor }}
            />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
